<template>
  <!-- background-sec1 안에 넣을거라 absolute로 섹션 기준 우측 배치 -->
  <div 
    class="hero-drawer" 
    aria-label="메인 배너 사이드바"
    ref="drawerRef"
    >
    <!-- 탭(항상 보임) -->
    <button
      class="hero-drawer__tab"
      type="button"
      @click="toggle"
      :aria-expanded="isOpen ? 'true' : 'false'"
      aria-controls="heroDrawerPanel"
      :aria-label="isOpen ? '사이드바 닫기' : '사이드바 열기'"
    >
      <span class="chev" :class="{ 'is-open': isOpen }" aria-hidden="true"></span>
    </button>

    <!-- 진짜 접힘: 닫혀있으면 패널 DOM 자체가 없음 -->
    <Transition name="drawer">
      <aside
        v-if="isOpen"
        id="heroDrawerPanel"
        class="hero-drawer__panel"
        aria-label="배너 목록"
      >
        <div v-if="loading" class="hero-drawer__state">
          <p>불러오는 중...</p>
        </div>

        <div v-else-if="!bannersForView.length" class="hero-drawer__state">
          <p>표시할 배너가 없습니다.</p>
        </div>

        <div v-else class="hero-drawer__grid">
          <a
            v-for="item in bannersForView"
            :key="item.sideBannerId"
            class="hero-drawer__item"
            :href="item.link"
            target="_blank"
            rel="noopener noreferrer"
            :aria-label="item.title || '사이드 배너'"
          >
            <div class="hero-drawer__thumb">
              <img
                class="hero-drawer__img"
                :src="toImgUrl(item.bannerImgFile1)"
                :alt="item.title || '사이드 배너 이미지'"
                loading="lazy"
              />
            </div>
          </a>
        </div>
      </aside>
    </Transition>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount } from "vue";
import { sideBannerList } from "@/api/banner";

const API_ORIGIN = process.env.VUE_APP_API_URL;


const isOpen = ref(false);
const loading = ref(false);
const rawBanners = ref([]);
const drawerRef = ref(null);

const toggle = () => {
  isOpen.value = !isOpen.value;
};

const bannersForView = computed(() => {
  return (rawBanners.value || [])
    .filter((b) => b?.status === "PUBLISHED")
    .filter((b) => !!b?.bannerImgFile1)
    .sort((a, b) => {
      if (a?.pinned !== b?.pinned) return (b?.pinned ? 1 : 0) - (a?.pinned ? 1 : 0);
      return (a?.no ?? 0) - (b?.no ?? 0);
    })
    .slice(0, 4);
});

const toImgUrl = (path) => {
  if (!path) return "";
  if (/^https?:\/\//i.test(path)) return path;
  return `${API_ORIGIN}${path}`;
};

const handleClickOutside = (e) => {
  if (!isOpen.value) return;
  if (!drawerRef.value) return;

  // 드로어 내부 클릭이면 무시
  if (drawerRef.value.contains(e.target)) return;

  // 바깥 클릭 → 닫기
  isOpen.value = false;
};

const getSideBannerList = async () => {
  try {
    loading.value = true;
    const params = {
      pageNo: 1,
      countPerRecord: 10,
      search: "",
      typeCd: "all",
      status: "PUBLISHED",
    };
    const result = await sideBannerList(params);
    rawBanners.value = result?.result === "ok" ? result?.data || [] : [];
  } catch (e) {
    rawBanners.value = [];
    console.error("사이드 배너 조회 오류:", e);
  } finally {
    loading.value = false;
  }
};

onMounted(() => {
  getSideBannerList();
  document.addEventListener("mousedown", handleClickOutside);
});

onBeforeUnmount(() => {
  document.removeEventListener("mousedown", handleClickOutside);
});
</script>

<style scoped>
/* 섹션(.background-sec1)이 position: relative; 이므로, 섹션 기준 우측 배치 */
.hero-drawer {
  position: absolute;
  top: 0px;
  right: 0px;
  z-index: 3;

  display: grid;
  grid-auto-flow: column;
  grid-auto-columns: auto;
  align-items: start;
  /* gap: 8px; */

  font-family: "Noto Sans KR", sans-serif;

}

/* 탭(버튼) */
.hero-drawer__tab {
  width: 50px;
  height: 50px;
  border: 0;
  cursor: pointer;

  border-radius: 100px;
  background: rgba(15, 23, 42, 0.92);
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.18);
  

  display: grid;
  place-items: center;
}

.hero-drawer__tab:hover {
  background: rgba(15, 23, 42, 0.98);
}

/* Chevron (아이콘) */
.chev {
  width: 10px;
  height: 10px;
  border-right: 2px solid rgba(255, 255, 255, 0.92);
  border-bottom: 2px solid rgba(255, 255, 255, 0.92);
  transform: rotate(135deg); /* 닫힘: 열기 방향 */
  transition: transform 200ms ease;
}

.chev.is-open {
  transform: rotate(-45deg); /* 열림: 닫기 방향 */
}

/* 패널(헤더 없음) */
.hero-drawer__panel {
  width: 250px;
  /* max-width: 100vw; */

  background: rgba(15, 23, 42, 0.78);
  backdrop-filter: blur(8px);
  border-radius: 3px;
  padding: 10px;
  opacity: 85%;

  border: 5px solid rgba(41, 62, 112, 0.98);

}

/* 목록 */
.hero-drawer__grid {
  display: grid;
  grid-template-columns: 1fr;
  /* gap: 10px; */
}

.hero-drawer__item {
  display: grid;
  text-decoration: none;
}

/* 이미지 고정 영역 */
.hero-drawer__thumb {
  width: 100%;
  height: 65px; /* ✅ 고정값 */
  /* border-radius: 12px; */
  overflow: hidden;
  /* background: rgba(255, 255, 255, 0.06); */
}

.hero-drawer__img {
  width: 100%;
  height: 100%;
  display: block;
  object-fit: contain;      /* ✅ 전체 보이게 */
  object-position: center;
}

/* 상태 텍스트 */
.hero-drawer__state p {
  font-size: 0.95rem;
  color: rgba(255, 255, 255, 0.92);
  margin: 0;
}

/* --- “진짜 접힘” 트랜지션: 패널이 생성/삭제되며 펼쳐짐 --- */
/* enter: 0폭에 가까운 상태에서 확장 + 약간 슬라이드 */
.drawer-enter-from,
.drawer-leave-to {
  opacity: 0;
  transform: translateX(10px);
  max-width: 0;
}

/* enter-to / leave-from: 정상 폭 */
.drawer-enter-to,
.drawer-leave-from {
  opacity: 1;
  transform: translateX(0);
  max-width: 250px;
}

/* 트랜지션 */
.drawer-enter-active,
.drawer-leave-active {
  transition: max-width 220ms ease, opacity 180ms ease, transform 220ms ease;
  overflow: hidden; /* 접히는 동안 내부가 삐져나오지 않게 */
}

/* 반응형 */
@media (max-width: 768px) {
  .hero-drawer {
    top: 12px;
    right: 12px;
  }

  .hero-drawer__panel {
    width: 248px;
  }

  .drawer-enter-to,
  .drawer-leave-from {
    max-width: 248px;
  }

  .hero-drawer__thumb {
    height: 84px;
  }

  .hero-drawer__tab {
    width: 34px;
    height: 60px;
    border-radius: 13px;
  }
}
</style>
